import { useState, useRef, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Bot, User as UserIcon } from "lucide-react";
import { API } from "@/lib/api";

const QUICK_PROMPTS = [
  "Detect payroll anomalies this period",
  "Explain federal, state & FICA deductions",
  "Forecast PTO liability for my team",
  "Am I compliant with overtime rules?",
];

export function AIAssistantPanel({ open, onOpenChange }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm RightPay AI. Ask me about payroll anomalies, tax deductions, PTO forecasting or compliance." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const data = JSON.parse(line);
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
                return copy;
              });
            }
          } catch (_) {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I ran into an error. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="ai-hr-assistant-panel"
        className="w-full sm:max-w-md flex flex-col p-0 gap-0 bg-card"
      >
        <SheetHeader className="p-5 border-b border-border">
          <SheetTitle className="flex items-center gap-2 font-display">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/10 text-primary">
              <Sparkles className="w-5 h-5" />
            </span>
             RightPay AI Assistant
          </SheetTitle>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <span className={`grid place-items-center w-8 h-8 rounded-lg shrink-0 ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                {m.role === "user" ? <UserIcon className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
              </span>
              <div className={`rounded-xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap max-w-[80%] ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-border space-y-3">
          <div className="flex flex-wrap gap-2">
            {QUICK_PROMPTS.map((p) => (
              <button
                key={p}
                data-testid="ai-quick-prompt"
                onClick={() => send(p)}
                disabled={streaming}
                className="text-xs px-2.5 py-1.5 rounded-full border border-border bg-secondary hover:bg-accent transition-colors disabled:opacity-50"
              >
                {p}
              </button>
            ))}
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2"
          >
            <Input
              data-testid="ai-assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about payroll, taxes, PTO…"
              className="flex-1"
            />
            <Button data-testid="ai-assistant-submit-btn" type="submit" size="icon" disabled={streaming}>
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
